import type { Node as PMNode, ResolvedPos } from '@atlaskit/editor-prosemirror/model';

export type SuggestionCardAnchorRange = { endPos: number; startPos: number };

export type SuggestionCardAnchorRanges = {
	/**
	 * Range to anchor to when `primary`'s anchors resolve to nothing. Absent when `primary` is
	 * already outside everything the diff could hide, so the two would coincide.
	 */
	fallback?: SuggestionCardAnchorRange;
	/**
	 * Closest range to the target, and the one the card lines up with whenever its anchors are
	 * rendered.
	 */
	primary?: SuggestionCardAnchorRange;
};

/**
 * Whether an anchor widget can be rendered directly inside `node`, which the schema already
 * knows: a text block holds the span inline, and a block container holds it in an anonymous
 * block box. Only a structural container — `table`, `tableRow`, `layoutSection`, a list —
 * accepts neither, and its DOM element reparents the span into an anonymous row or cell
 * (EDITOR-8442) or turns it into an extra grid item.
 */
function canHostAnchor(node: PMNode): boolean {
	return (
		node.type.inlineContent ||
		node.type.contentMatch.matchType(node.type.schema.nodes.paragraph) !== null
	);
}

/**
 * The position within `range` closest to its `edge` whose parent can host an anchor widget.
 * Each step of one token enters the first (or last) child of the container that rejected the
 * previous position, so a cell resolves immediately and a row in one step. `undefined` when the
 * whole range is unhostable, i.e. a leaf or a container of leaves.
 */
function toHostableAnchorPos(
	doc: PMNode,
	range: { end: number; start: number },
	edge: 'end' | 'start',
): number | undefined {
	const step = edge === 'start' ? 1 : -1;

	for (
		let pos = edge === 'start' ? range.start : range.end;
		pos >= range.start && pos <= range.end;
		pos += step
	) {
		if (canHostAnchor(doc.resolve(pos).parent)) {
			return pos;
		}
	}

	return undefined;
}

/**
 * The boundary of the node at `fromDepth`, moved outward while the enclosing node cannot host an
 * anchor widget. `$pos.node(0)` is the doc, which always can, so this always resolves — at worst
 * to the target's own top-level block.
 */
function toOutwardAnchorRange($pos: ResolvedPos, fromDepth: number): SuggestionCardAnchorRange {
	let depth = fromDepth;
	while (depth > 1 && !canHostAnchor($pos.node(depth - 1))) {
		depth--;
	}
	return { startPos: $pos.before(depth), endPos: $pos.after(depth) };
}

/**
 * Pairs a primary range with its outward fallback, dropping the fallback when it would resolve to
 * the same positions — no point emitting a second anchor pair on top of the first — and promoting
 * it when there is no primary at all, which happens for a leaf target with no content to sit in.
 */
function withOutwardFallback(
	primary: SuggestionCardAnchorRange | undefined,
	outward: SuggestionCardAnchorRange,
): SuggestionCardAnchorRanges {
	if (!primary) {
		return { primary: outward };
	}
	return primary.startPos === outward.startPos && primary.endPos === outward.endPos
		? { primary }
		: { primary, fallback: outward };
}

/**
 * Pin the suggestion card's anchor spans to the boundary of a meaningful enclosing block instead
 * of the deep inner target node, so the card lines up with the block the user actually sees.
 *
 * Returns up to two ranges. `primary` is the closest usable range; `fallback` is an outward one
 * for the case where `primary` sits inside content that is rendered but hidden —
 * `editor-plugin-show-diff` puts `display: none` on the nodes an inverted, hide-deleted diff
 * suppresses, which is the target node itself for a `removeNode` suggestion, and that is the diff
 * this plugin asks for (see `showDiff({ isInverted: true, hideDeletedDiffs })` in
 * `select-suggestion.ts`). An anchor inside a `display: none` subtree generates no anchor box, so
 * the consumer chains the two with `anchor()`'s fallback argument. Both are empty when `pos` is
 * not inside a block worth pinning to, and the caller uses the target's own boundary.
 */
export function findSuggestionCardAnchorRanges(
	doc: PMNode,
	pos: number,
): SuggestionCardAnchorRanges {
	const $pos = doc.resolve(pos);
	let outermostExpandDepth: number | undefined;
	let tableCellDepth: number | undefined;

	for (let depth = $pos.depth; depth > 0; depth--) {
		const typeName = $pos.node(depth).type.name;
		if (typeName === 'expand' || typeName === 'nestedExpand') {
			outermostExpandDepth = depth;
		} else if (
			(typeName === 'tableCell' || typeName === 'tableHeader') &&
			tableCellDepth === undefined
		) {
			tableCellDepth = depth;
		}
	}

	// An enclosing expand takes precedence over a table (it can itself contain
	// one), and the icon button renders on the expand, not content buried in
	// its body — so its OUTER boundary (before/after) is the line-up target.
	if (outermostExpandDepth !== undefined) {
		return withOutwardFallback(
			{
				startPos: $pos.before(outermostExpandDepth),
				endPos: $pos.after(outermostExpandDepth),
			},
			toOutwardAnchorRange($pos, outermostExpandDepth),
		);
	}

	// `pos` is the position *before* the anchor node, so `$pos` resolves into the anchor's PARENT
	// and the anchor node itself is absent from the ancestor chain above. When that parent cannot
	// host the span, the anchor node's own boundary is unusable — a `<tr>` between cells, a
	// `<table>` between rows, a layout between columns — and that is exactly where a suggestion
	// targeting one of those nodes directly (`setAttrs` on a cell background or `colwidth`,
	// `removeNode` of a spanning cell) would otherwise be anchored.
	if (!canHostAnchor($pos.parent)) {
		const nodeAtPos = doc.nodeAt(pos);
		const innerRange = nodeAtPos
			? { start: pos + 1, end: pos + nodeAtPos.nodeSize - 1 }
			: undefined;
		const innerStartPos = innerRange && toHostableAnchorPos(doc, innerRange, 'start');
		const innerEndPos = innerRange && toHostableAnchorPos(doc, innerRange, 'end');

		// Reaching into the target's content keeps the card aligned with the target itself, which
		// is why it is preferred — but it is also the range the diff can hide, hence the outward
		// fallback.
		return withOutwardFallback(
			innerStartPos !== undefined && innerEndPos !== undefined
				? { startPos: innerStartPos, endPos: innerEndPos }
				: undefined,
			toOutwardAnchorRange($pos, $pos.depth),
		);
	}

	if (tableCellDepth === undefined) {
		return {};
	}

	// Anchor INSIDE the cell's content (start/end), not at either boundary:
	// the cell's outer boundary sits between <td> siblings inside <tr>
	// (extra CSS grid item, collapses cells — EDITOR-8442), and the enclosing
	// row's boundary sits between <tr> siblings inside <tbody> (browser wraps
	// the invalid child in an anonymous row, breaking rowspan continuation).
	return withOutwardFallback(
		{
			startPos: $pos.start(tableCellDepth),
			endPos: $pos.end(tableCellDepth),
		},
		toOutwardAnchorRange($pos, tableCellDepth),
	);
}
